import React from 'react';
import {Button, Col, Container, Row} from "react-bootstrap";
import {useHistory, withRouter} from "react-router-dom";
import * as Yup from "yup";
import {Formik} from "formik";
import {registerAbsence} from "../../../services/enrollments";
import {useAuth} from "../../../contexts/authContext";
import moment from 'moment';
import MySwal from "sweetalert2";
import {Trans, useTranslation} from "react-i18next";
import {checkDate} from "../../../utils";

// React Components
import Header from "../../../components/Header";
import InputSelect from "../../../components/InputSelect";
import DateTimePicker from "../../../components/DateTimePicker";
import ErrorMessage from "../../../components/ErrorMessage";
import CheckBox from "../../../components/CheckBox";

// CSS Files
import "./styles.css";

const NotifyAbsence = () => {
   const { user } = useAuth();
   const history = useHistory();
   const { t } = useTranslation();

   const reasons = [
      t("notify_absence_page.absence_reasons.injury"),
      t("notify_absence_page.absence_reasons.work_vacation"),
      t("notify_absence_page.absence_reasons.looking_for_sub"),
      t("notify_absence_page.absence_reasons.want_to_quit")
   ];

   const sendAbsence = async (values) => {
      await registerAbsence(user.active_enrollment.id, values.reason, values.absenceStartDate, values.absenceEndDate);
      MySwal.fire({
         allowOutsideClick: false,
         titleText: t("common.success"),
         text: t("notify_absence_page.success")
      }).then(() => {
         history.push("/availabilities", {leagueEditionEndDate: history.location.state.leagueEditionEndDate});
      });
   }

   return <div>
      <Header headerBarTitle={t("notify_absence_page.header")} showHeaderExtras/>

      <Container className={"desktop"}>
         <Row>
            <Col className={"text-center warning"}>
               <p>{t("notify_absence_page.absence_report_note")}</p>
            </Col>
         </Row>

         <Row className={"mt-3"}>
            <Col>
               <Formik
                  initialValues={{
                     reason: '',
                     absenceStartDate: checkDate(new Date(), new Date(history.location.state.leagueEditionStartDate), new Date(history.location.state.leagueEditionEndDate)),
                     absenceEndDate: checkDate(new Date(), new Date(history.location.state.leagueEditionStartDate), new Date(history.location.state.leagueEditionEndDate)),
                     hasReadWarnings: false
                  }}
                  validationSchema={Yup.object({
                     reason: Yup.string()
                     .required(t("common.form.errors.mandatory_field")),
                     absenceStartDate: Yup.date()
                     .required(t("common.form.errors.mandatory_field")),
                     absenceEndDate: Yup.date()
                     .required(t("common.form.errors.mandatory_field"))
                     .when('reason', {
                        is: (value) => value === reasons[0] || value === reasons[1],
                        then: Yup.date()
                        .test(
                           'absenceTooShort',
                           t("notify_absence_page.errors.absence_end_error"),
                           function (value) {
                              return moment(value).diff(moment(this.parent.absenceStartDate).startOf('day'), 'days') >= 4
                           }
                        )
                     }),
                     hasReadWarnings: Yup.bool()
                     .oneOf([true], t("notify_absence_page.errors.absence_disclaimer_error"))
                  })}
                  onSubmit={async (values) => {
                     let isAbsenceTooLong = false;

                     if (history.location.state.absenceStartDate) {
                        let daysLastAbsence = moment().diff(moment(history.location.state.absenceStartDate).startOf('day'), 'days');
                        let daysNewAbsence = moment(values.absenceEndDate).diff(moment(values.absenceStartDate).startOf('day'), 'days');

                        isAbsenceTooLong = daysLastAbsence + daysNewAbsence > 14;
                     } else {
                        isAbsenceTooLong = moment(values.absenceEndDate).diff(moment(values.absenceStartDate).startOf('day'), 'days') > 14;
                     }

                     if (isAbsenceTooLong) {
                        MySwal.fire({
                           allowOutsideClick: false,
                           showCancelButton: true,
                           titleText: t("common.warning"),
                           text: t("notify_absence_page.errors.absence_too_long"),
                           confirmButtonText: "Ok",
                           cancelButtonText: t("common.cancel")
                        }).then(async (res) => {
                           if (res.dismiss === MySwal.DismissReason.cancel) {
                              MySwal.close();
                           } else {
                              await sendAbsence(values);
                           }
                        });
                     } else {
                        await sendAbsence(values);
                     }
                  }}
               >
                  {formik => (
                     <form onSubmit={formik.handleSubmit}>
                        <InputSelect text={t("notify_absence_page.labels.absence_reason")}>
                           <select className="form-control mb-2" name="reason" value={formik.values.reason} onChange={formik.handleChange}>
                              <option value="" disabled>{t("common.form.options.choose_an_option")}</option>
                              {reasons.map((reason, key) => {
                                 return <option key={key} value={reason}>{reason}</option>
                              })}
                           </select>
                        </InputSelect>
                        <ErrorMessage
                           errors={formik.errors}
                           touched={formik.touched}
                           name="reason"
                        />

                        {formik.values.reason === reasons[1] &&
                           <>
                              <DateTimePicker
                                 wrapperClasses={"form-group"}
                                 datePickerLabel={t("notify_absence_page.labels.absence_start")}
                                 dateFormat={"dd/MM/yyyy"}
                                 showTimeSelect={false}
                                 disabled={formik.values.reason === reasons[0]}
                                 minDate={new Date() < new Date(history.location.state.leagueEditionStartDate) ? new Date(history.location.state.leagueEditionStartDate) : new Date()}
                                 maxDate={new Date(history.location.state.leagueEditionEndDate)}
                                 date={formik.values.absenceStartDate}
                                 onChange={(e) => formik.setFieldValue('absenceStartDate', e)}
                              />
                              <ErrorMessage
                                 errors={formik.errors}
                                 touched={formik.touched}
                                 name="absenceStartDate"
                              />
                           </>
                        }

                        {(formik.values.reason === reasons[0] || formik.values.reason === reasons[1]) &&
                           <>
                              <DateTimePicker
                                 wrapperClasses={"form-group"}
                                 datePickerLabel={t("notify_absence_page.labels.absence_end")}
                                 dateFormat={"dd/MM/yyyy"}
                                 showTimeSelect={false}
                                 minDate={new Date() < new Date(history.location.state.leagueEditionStartDate) ? new Date(history.location.state.leagueEditionStartDate) : new Date()}
                                 maxDate={new Date(history.location.state.leagueEditionEndDate)}
                                 date={formik.values.absenceEndDate}
                                 onChange={(e) => formik.setFieldValue('absenceEndDate', e)}
                              />

                              <ErrorMessage
                                 errors={formik.errors}
                                 touched={formik.touched}
                                 name="absenceEndDate"
                              />

                              <Row className={"mt-3 mb-3"}>
                                 <Col className={"warning"}>
                                    <p>
                                       <Trans i18nKey="notify_absence_page.absence_note" components={{br: <br/>}}/>
                                    </p>
                                 </Col>
                              </Row>
                           </>
                        }

                        {formik.values.reason === reasons[2] &&
                           <Row className={"mt-3 mb-3"}>
                              <Col className={"warning"}>
                                 <p>
                                    <Trans i18nKey="notify_absence_page.absence_note_sub" components={{br: <br/>}}/>
                                 </p>
                              </Col>
                              <br/>
                           </Row>
                        }

                        {formik.values.reason === reasons[3] &&
                           <Row className={"mt-3 mb-3"}>
                              <Col className={"warning"}>
                                 <p>
                                    <Trans i18nKey="notify_absence_page.absence_note_quit" components={{br: <br/>}}/>
                                 </p>
                              </Col>
                           </Row>
                        }

                        {formik.values.reason !== '' &&
                           <>
                              <CheckBox
                                 handleChange={formik.handleChange}
                                 checked={formik.values.hasReadWarnings}
                                 name="hasReadWarnings"
                                 text={t("notify_absence_page.absence_accept_disclaimer")}
                              />

                              <ErrorMessage
                                 errors={formik.errors}
                                 touched={formik.touched}
                                 name="hasReadWarnings"
                              />
                           </>
                        }

                        <Button type="submit" className="button button-background-primary button-text-style-primary submit-btn" disabled={formik.isSubmitting}>
                           {formik.isSubmitting ? t("common.please_wait") : t("common.save")}
                        </Button>
                     </form>
                  )}
               </Formik>
            </Col>
         </Row>
      </Container>
   </div>
}

export default withRouter(NotifyAbsence);